import type { ApiSchema } from "../../../api/apiSchema";
import type { ToolInterface, ToolParameter } from "../../../api/content/authoring/processContentDecoders";
import type { InputSlot, PlaceholderSource } from "../../../api/content/authoring/selectorDecoders";
import type { AgentStepDefinition, HumanStepDefinition, StepDefinition, ToolStepDefinition } from "../../../api/content/authoring/stepDecoders";
import { namesOnly, storedTranslations, withChoiceKey } from "../draft/bilingualTexts";

/**
 * The step form's model (authoring-ui.md AU-4): pure functions from a step definition
 * and one user action to the next definition. The form keeps the definition itself as
 * its draft, so what the user sees in the definition panel is exactly what is saved.
 *
 * Wiring rules (W-2, W-4, P-3): every placeholder of an agent step's prompt and every
 * input of a tool step's tool has exactly one source, a selector or an input slot. An
 * input slot is declared with the kinds it accepts; a slot nothing reads any more is
 * dropped with the source that named it.
 */

export type StepKind = StepDefinition["kind"];

export const STEP_FORM_KINDS: readonly StepKind[] = ["tool", "agent", "human"];

/** Placeholder and slot names (W-2): lower case, digits and `_`, starting with a letter. */
export const KEY_NAME_PATTERN = /^[a-z][a-z0-9_]{0,62}$/;

const PLACEHOLDER = /\{([a-z][a-z0-9_]{0,62})\}/y;

/**
 * The placeholders of a prompt text in order of first use, as the server reads them:
 * `{{name}}` and `{{` are escapes, not placeholders.
 */
export function placeholderNames(text: string): readonly string[] {
  const names: string[] = [];
  let position = 0;
  while (position < text.length) {
    if (text.startsWith("{{", position)) {
      const closing = /^\{\{[a-z][a-z0-9_]{0,62}\}\}/.exec(text.slice(position));
      position += closing === null ? 2 : closing[0].length;
      continue;
    }
    PLACEHOLDER.lastIndex = position;
    const match = PLACEHOLDER.exec(text);
    if (match?.[1] === undefined) {
      position += 1;
      continue;
    }
    if (!names.includes(match[1])) {
      names.push(match[1]);
    }
    position = PLACEHOLDER.lastIndex;
  }
  return names;
}

/** An empty step of a kind, with what a new step of that kind needs to be valid. */
export function newStepDefinition(kind: StepKind, name = "", description = ""): StepDefinition {
  switch (kind) {
    case "tool":
      return { kind, name, description, tool: "", inputs: {}, input_slots: {}, parameters: {}, propose: null, requirements: [], limits: null };
    case "human":
      return { kind, name, description, instructions: "", inputs: {}, response: { kind: "text" } };
    case "agent":
      return {
        kind,
        name,
        description,
        prompt: "",
        placeholders: {},
        inputs: {},
        skills: [],
        tools: { dmw: [], item_tools: [], skill_servers: [] },
        output: { kind: "text" },
        session: { mode: "isolated" },
        // "Save as step" uses the same plain defaults (reviews.md R-8).
        context_compaction: { method: "none", version: 1, parameters: {} },
        requirements: [{ capability: "text", equals: true }],
        limits: null,
      };
    case "talk":
      return { kind, name, description, prompt: "" };
  }
}

/** Another kind keeps the name and the description; the rest starts empty. */
export function withKind(step: StepDefinition, kind: StepKind): StepDefinition {
  if (step.kind === kind) return step;
  // The name and description carry over in every language; what belongs to one kind doesn't.
  const translations = namesOnly(step.translations);
  const next = newStepDefinition(kind, step.name, step.description);
  return translations === undefined ? next : { ...next, translations };
}

/** Whether a person step's choice at `index` may take `key`: a valid key no other choice has. */
export function choiceKeyFree(step: HumanStepDefinition, index: number, key: string): boolean {
  const response = step.response;
  return KEY_NAME_PATTERN.test(key) && response.kind === "choice" && response.options.every((option, position) => position === index || option.key !== key);
}

/** The step with its choice at `index` under `key`, its labels in every language moving with it; the caller checks `choiceKeyFree` first. */
export function withChoiceRenamed(step: HumanStepDefinition, index: number, key: string): HumanStepDefinition {
  const response = step.response;
  const option = response.kind === "choice" ? response.options[index] : undefined;
  if (response.kind !== "choice" || option === undefined) return step;
  const options = response.options.map((other, position) => (position === index ? { ...other, key } : other));
  return { ...step, response: { ...response, options }, translations: storedTranslations(step.translations, withChoiceKey(step.translations, option.key, key)) };
}

/** The item content a step is saved and checked as. */
export function stepContentOf(step: StepDefinition): ApiSchema<"StepContent"> {
  return { kind: "step", definition: step };
}

// --- Tool steps -----------------------------------------------------------------------

/**
 * Point a tool step at a tool (a tool `VersionRef` or `builtin:<name>@<version>`). Inputs
 * the tool still declares keep their sources; a required input without one is wired to an
 * input slot of the same name, for a Workflow to bind. Parameters the tool does not
 * declare are dropped, so the tool's defaults apply.
 */
export function withTool(step: ToolStepDefinition, tool: string, declared: ToolInterface): ToolStepDefinition {
  let next: ToolStepDefinition = { ...step, tool, inputs: {}, input_slots: {}, parameters: pick(step.parameters, Object.keys(declared.parameters)) };
  for (const [name, input] of Object.entries(declared.inputs)) {
    const kept = step.inputs[name];
    if (kept !== undefined) {
      next = withToolInputSource(next, name, kept, input.accepts, input.required, step.input_slots);
    } else if (input.required) {
      next = withToolInputSource(next, name, { source: "input", input: name }, input.accepts, input.required);
    }
  }
  return next;
}

/**
 * Wire one tool input to a source, or unwire it with `null`. An input slot is declared with
 * the kinds the tool input accepts, unless `knownSlots` already declares it.
 */
export function withToolInputSource(
  step: ToolStepDefinition,
  input: string,
  source: PlaceholderSource | null,
  accepts: InputSlot["accepts"],
  required: boolean,
  knownSlots: ToolStepDefinition["input_slots"] = step.input_slots,
): ToolStepDefinition {
  const inputs = source === null ? without(step.inputs, input) : { ...step.inputs, [input]: source };
  const slots: Record<string, InputSlot> = {};
  for (const wired of Object.values(inputs)) {
    if (wired.source === "input") {
      slots[wired.input] = knownSlots[wired.input] ?? step.input_slots[wired.input] ?? { accepts: [...accepts], required };
    }
  }
  return { ...step, inputs, input_slots: slots };
}

/** Set a parameter, or remove it with `undefined` so the tool's default applies. */
export function withParameter(step: ToolStepDefinition, name: string, value: unknown): ToolStepDefinition {
  return { ...step, parameters: value === undefined ? without(step.parameters, name) : { ...step.parameters, [name]: value } };
}

export type ParameterProblem = "notBoolean" | "notInteger" | "notNumber" | "belowMinimum" | "aboveMaximum" | "patternMismatch" | "notAllowed";

/** Every parameter problem, for their texts. */
export const PARAMETER_PROBLEMS: readonly ParameterProblem[] = ["notBoolean", "notInteger", "notNumber", "belowMinimum", "aboveMaximum", "patternMismatch", "notAllowed"];

/** Whether a value fits a declared parameter: the form's hint before the server checks. */
export function parameterProblem(parameter: ToolParameter, value: unknown): ParameterProblem | null {
  switch (parameter.type) {
    case "boolean":
      return typeof value === "boolean" ? null : "notBoolean";
    case "integer":
    case "number": {
      if (typeof value !== "number" || Number.isNaN(value)) {
        return parameter.type === "integer" ? "notInteger" : "notNumber";
      }
      if (parameter.type === "integer" && !Number.isInteger(value)) {
        return "notInteger";
      }
      if (parameter.minimum !== null && parameter.minimum !== undefined && value < parameter.minimum) {
        return "belowMinimum";
      }
      if (parameter.maximum !== null && parameter.maximum !== undefined && value > parameter.maximum) {
        return "aboveMaximum";
      }
      return null;
    }
    case "string":
      if (typeof value !== "string") {
        return "patternMismatch";
      }
      return parameter.pattern === null || parameter.pattern === undefined || new RegExp(parameter.pattern, "u").test(value) ? null : "patternMismatch";
    case "enum":
      return typeof value === "string" && parameter.values.includes(value) ? null : "notAllowed";
  }
}

// --- Agent steps ----------------------------------------------------------------------

/**
 * Change the prompt's placeholders to the names found in its text: names still used keep
 * their sources, a new name is wired to an input slot of that name accepting text, and
 * slots no placeholder reads any more are dropped.
 */
/** The step pinning its own agent instructions where they are a chosen prompt version; without the
 * field for none or a new prompt, which the save pins once it is written (WD-1+3 D-2). */
export function withAgentInstructions(step: AgentStepDefinition, instructions: string | null): AgentStepDefinition {
  const { agent_instructions: _replaced, ...rest } = step;
  return instructions === null || instructions === "" ? rest : { ...rest, agent_instructions: instructions };
}

export function withPlaceholdersOf(step: AgentStepDefinition, text: string): AgentStepDefinition {
  const names = placeholderNames(text);
  const placeholders: Record<string, PlaceholderSource> = {};
  const inputs: Record<string, InputSlot> = {};
  for (const name of names) {
    const source = step.placeholders[name] ?? { source: "input", input: name };
    placeholders[name] = source;
    if (source.source === "input") {
      inputs[source.input] = step.inputs[source.input] ?? { accepts: ["text"], required: true };
    }
  }
  return { ...step, placeholders, inputs };
}

/** Wire one placeholder to a source; an input slot is declared as `slot`, or kept. */
export function withPlaceholderSource(step: AgentStepDefinition, name: string, source: PlaceholderSource, slot?: InputSlot): AgentStepDefinition {
  const placeholders = { ...step.placeholders, [name]: source };
  const inputs: Record<string, InputSlot> = {};
  for (const wired of Object.values(placeholders)) {
    if (wired.source === "input") {
      inputs[wired.input] = (wired === source ? slot : undefined) ?? step.inputs[wired.input] ?? { accepts: ["text"], required: true };
    }
  }
  return { ...step, placeholders, inputs };
}

/** Allow or forbid one DMW tool, keeping the catalogue's order out of it: sorted by name. */
export function withDmwTool(step: AgentStepDefinition, tool: string, allowed: boolean): AgentStepDefinition {
  const dmw = new Set(step.tools.dmw);
  if (allowed) {
    dmw.add(tool);
  } else {
    dmw.delete(tool);
  }
  return { ...step, tools: { ...step.tools, dmw: [...dmw].sort() } };
}

// --- Human steps ----------------------------------------------------------------------

/** Declare, change or remove (`null`) one input a human step shows. */
export function withHumanInput(step: HumanStepDefinition, name: string, slot: InputSlot | null): HumanStepDefinition {
  const inputs = slot === null ? without(step.inputs, name) : { ...step.inputs, [name]: slot };
  // A review of an input that is gone falls back to a free-text answer.
  const response = step.response.kind === "review_output" && !(step.response.input in inputs) ? { kind: "text" as const } : step.response;
  return { ...step, inputs, response };
}

function without<T>(record: Readonly<Record<string, T>>, name: string): Record<string, T> {
  return Object.fromEntries(Object.entries(record).filter(([member]) => member !== name));
}

function pick<T>(record: Readonly<Record<string, T>>, names: readonly string[]): Record<string, T> {
  return Object.fromEntries(Object.entries(record).filter(([name]) => names.includes(name)));
}
